import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { fetchActivePromoImages } from "../api/promosApi";
import { getAvailableSubscriptionPlans } from "../api/subscriptionApi";
import PromoCarousel from "../components/PromoCarousel";
import "./css/AbbonamentiCliente.css";

const fallbackSlides = [
  { img: "/images/03-hero-promo-benvenuto.png", title: "Promo benvenuto" },
  { img: "/images/03-hero_02.png", title: "Stirup" },
  { img: "/images/03-hero_03.png", title: "Stirup" },
];

const euro = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return "—";

  return (value / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function getPresentation(plan) {
  return plan?.config?.presentation || {};
}

function getPlanTitle(plan) {
  return getPresentation(plan).title || plan.name || "Abbonamento";
}

function getPlanShort(plan) {
  return getPresentation(plan).short_description || plan.description || "";
}

function getPlanColor(plan) {
  return getPresentation(plan).theme_color || "#0877d9";
}

function getPlanRows(plan) {
  const rows = getPresentation(plan).rows;
  if (Array.isArray(rows) && rows.length) return rows;

  return [
    { icon: "👕", label: "N° capi max", value: plan?.config?.max_items ?? "—" },
    { icon: "💶", label: "Costo al mese", value: euro(plan.price_cents) },
  ];
}

function getPlanConditions(plan) {
  const conditions = getPresentation(plan).conditions;
  return Array.isArray(conditions) ? conditions : [];
}

function imageForPlan(plan) {
  const key = getPresentation(plan).image_key;

  if (key === "folded-small") return "/images/folded-small.png";
  if (key === "folded-medium") return "/images/folded-medium.png";
  if (key === "folded-large") return "/images/folded-large.png";

  return plan.image_url || "/images/folded-small.png";
}

export default function AbbonamentiConfronto() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [slides, setSlides] = useState(fallbackSlides);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const visiblePlans = useMemo(
    () =>
      plans.filter((p) => Array.isArray(p.suppliers) && p.suppliers.length > 0),
    [plans],
  );

  const allConditions = useMemo(() => {
    const out = [];
    visiblePlans.forEach((plan) => {
      getPlanConditions(plan).forEach((c) => {
        if (c && !out.includes(c)) out.push(c);
      });
    });

    return out.length
      ? out
      : [
          "Il numero indicato è il massimo dei capi che non può essere superato per ogni consegna.",
          "Se consegni meno capi non ricevi sconti.",
          "Eventuali capi extra o speciali possono essere addebitati separatamente.",
        ];
  }, [visiblePlans]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await getAvailableSubscriptionPlans(token);
        setPlans(Array.isArray(data?.plans) ? data.plans : []);
      } catch (err) {
        toast.error(err.message || "Errore caricamento abbonamenti");
        setPlans([]);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [token]);

  useEffect(() => {
    async function loadSlides() {
      try {
        const promos = await fetchActivePromoImages({
          token,
          limit: 6,
        });

        const mapped = Array.isArray(promos)
          ? promos
              .filter((p) => p.image_url)
              .map((p) => ({
                id: p.id,
                img: p.image_url,
                title: p.code ? `Promo ${p.code}` : "Promo",
              }))
          : [];

        if (mapped.length) setSlides(mapped);
      } catch {
        setSlides(fallbackSlides);
      }
    }

    loadSlides();
  }, [token]);

  return (
    <main className="cap-page">
      <div className="cap-page-wrapper">
        <PromoCarousel slides={slides} />

        <section className="plans-content">
          <section className="plans-hero" aria-labelledby="plans-title">
            <div className="plans-flags" aria-hidden>
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
              <span className="plans-flag" />
            </div>

            <div className="plans-hero-copy">
              <div className="plans-kicker">📅 Calendario Anti Panico</div>

              <h1 className="plans-title" id="plans-title">
                Scegli <span>la misura</span>
              </h1>

              <p className="plans-subtitle">
                Confronta i piani disponibili e attiva quello più adatto alla
                tua routine. Il fornitore lo scegli nello step successivo.
              </p>
            </div>

            <div className="plans-hero-visual">
              <div className="plans-calendar" aria-hidden>
                <div className="plans-calendar-head">StirUp</div>
                <div className="plans-calendar-week">
                  <span>L</span>
                  <span>M</span>
                  <span>M</span>
                  <span>G</span>
                  <span>V</span>
                  <span>S</span>
                  <span>D</span>
                </div>
                <div className="plans-calendar-days">
                  {Array.from({ length: 21 }).map((_, i) => (
                    <span key={i} className={i === 9 ? "marked" : ""} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {loading ? (
            <div className="cap-empty">Caricamento piani...</div>
          ) : visiblePlans.length === 0 ? (
            <div className="cap-empty">
              Al momento non ci sono fornitori associati ai piani abbonamento.
            </div>
          ) : (
            <section className="plans-compare" id="confronta-misure">
              {visiblePlans.map((plan) => {
                const color = getPlanColor(plan);
                const rows = getPlanRows(plan);
                const cta = getPresentation(plan).cta_label || "Attiva ora";

                return (
                  <article
                    className="plan-card"
                    key={plan.id}
                    style={{
                      "--plan-color": color,
                    }}
                  >
                    <img
                      className="plan-card-img"
                      src={imageForPlan(plan)}
                      alt=""
                      aria-hidden
                    />

                    <h2 className="plan-name">{getPlanTitle(plan)}</h2>

                    <p className="plan-short">{getPlanShort(plan)}</p>

                    <div className="plan-table">
                      {rows.map((row, idx) => (
                        <div className="plan-row" key={`${plan.id}-${idx}`}>
                          <div className="plan-icon">{row.icon || "•"}</div>
                          <div>{row.label}</div>
                          <div className="plan-value">{row.value}</div>
                        </div>
                      ))}

                      <div className="plan-row">
                        <div className="plan-icon">💶</div>
                        <div>Costo al mese</div>
                        <div className="plan-value plan-price">
                          {euro(plan.price_cents)}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="plan-cta"
                      onClick={() => navigate(`/abbonamenti/attiva/${plan.id}`)}
                    >
                      {cta}
                      <span>›</span>
                    </button>
                  </article>
                );
              })}
            </section>
          )}

          <section className="conditions-box" id="condizioni-servizio">
            <div className="conditions-title">
              <div className="conditions-icon">i</div>
              Condizioni
              <br />
              del servizio
            </div>

            <div>
              <ul className="conditions-list">
                {allConditions.map((condition, idx) => (
                  <li key={idx}>{condition}</li>
                ))}
              </ul>

              <p className="conditions-note">
                Le condizioni possono variare in base al piano scelto e al
                fornitore selezionato nella fase di attivazione.
              </p>
            </div>
          </section>
        </section>
      </div>
    </main>
  );
}
